import Papa from 'papaparse';

const RECORD_TYPE_LABELS = {
  lesson_assessment: 'Lesson assessment',
  flight: 'Flight',
  theory_exam: 'Theory exam',
  skill_test: 'Skill test',
  credit_review: 'Credit review',
};

function getAssessmentTimestamp(entry) {
  const payloadTime = Date.parse(entry?.record_payload?.assessedAt || '');
  const recordedTime = Date.parse(entry?.assessed_at || '');
  return payloadTime || recordedTime || Date.parse(entry?.created_at || '') || 0;
}

export function currentTrainingRecordEntries(entries = []) {
  const supersededIds = new Set(
    entries
      .map((entry) => entry?.supersedes_entry_id)
      .filter(Boolean)
      .map(String)
  );

  return entries
    .filter((entry) => !supersededIds.has(String(entry.id)))
    .slice()
    .sort((left, right) => (
      String(left.recorded_on || '').localeCompare(String(right.recorded_on || '')) ||
      getAssessmentTimestamp(left) - getAssessmentTimestamp(right)
    ));
}

export function latestTrainingItemOutcomes(entries = []) {
  const latestByItem = new Map();
  currentTrainingRecordEntries(entries)
    .filter((entry) => entry.record_type === 'lesson_assessment')
    .forEach((entry) => {
      const assessmentTime = getAssessmentTimestamp(entry);
      const assessments = Array.isArray(entry.record_payload?.exerciseAssessments)
        ? entry.record_payload.exerciseAssessments
        : [];
      assessments.forEach((item) => {
        if (
          !item?.itemId ||
          !['satisfactory', 'needs_practice'].includes(item.outcome)
        ) return;

        const itemId = String(item.itemId);
        const previous = latestByItem.get(itemId);
        if (!previous || assessmentTime >= previous.assessedAt) {
          latestByItem.set(itemId, {
            outcome: item.outcome,
            assessedAt: assessmentTime,
          });
        }
      });
    });
  return latestByItem;
}

export function buildTrainingProgressSummary(exercises = [], entries = []) {
  const latestByItem = latestTrainingItemOutcomes(entries);
  let itemCount = 0;
  let satisfactoryCount = 0;
  let needsPracticeCount = 0;
  let notAssessedCount = 0;
  let exercisesWithoutCriteria = 0;

  exercises.forEach((exercise) => {
    const items = Array.isArray(exercise.items) ? exercise.items : [];
    if (items.length === 0) {
      exercisesWithoutCriteria += 1;
      return;
    }
    items.forEach((item) => {
      itemCount += 1;
      const outcome = latestByItem.get(String(item.id))?.outcome;
      if (outcome === 'satisfactory') satisfactoryCount += 1;
      else if (outcome === 'needs_practice') needsPracticeCount += 1;
      else notAssessedCount += 1;
    });
  });

  return {
    exerciseCount: exercises.length,
    itemCount,
    satisfactoryCount,
    needsPracticeCount,
    notAssessedCount,
    exercisesWithoutCriteria,
  };
}

export function buildTrainingRecordSummary(entries = []) {
  const currentEntries = currentTrainingRecordEntries(entries);
  const flights = currentEntries.filter((entry) => entry.record_type === 'flight');
  const sumMinutes = (classification) => flights
    .filter((entry) => entry.flight_classification === classification)
    .reduce((total, entry) => total + (Number(entry.flight_duration_minutes) || 0), 0);
  const theoryExams = currentEntries.filter((entry) => entry.record_type === 'theory_exam');
  const skillTests = currentEntries.filter((entry) => entry.record_type === 'skill_test');
  const creditMinutes = currentEntries
    .filter((entry) => entry.record_type === 'credit_review')
    .reduce((total, entry) => total + (Number(entry.credit_minutes) || 0), 0);

  return {
    dualMinutes: sumMinutes('dual'),
    soloMinutes: sumMinutes('solo'),
    simulatorMinutes: sumMinutes('simulator'),
    creditMinutes,
    theoryExamCount: theoryExams.length,
    theoryPassCount: theoryExams.filter((entry) => entry.outcome === 'pass').length,
    skillTestCount: skillTests.length,
    skillTestPassCount: skillTests.filter((entry) => entry.outcome === 'pass').length,
    entryCount: currentEntries.length,
  };
}

function recordRow(entry, context, assessmentItem = null) {
  const payload = entry.record_payload || {};
  return {
    student: context.studentName || '',
    course: context.courseName || '',
    syllabus_version: context.versionCode || '',
    date: entry.recorded_on || '',
    type: RECORD_TYPE_LABELS[entry.record_type] || entry.record_type || '',
    exercise_reference: assessmentItem?.exerciseReference || assessmentItem?.exercise_reference || '',
    exercise_title: assessmentItem?.exerciseTitle || assessmentItem?.exercise_title || '',
    item_reference: assessmentItem?.itemReference || assessmentItem?.item_reference || '',
    assessment_outcome: assessmentItem?.outcome || entry.outcome || '',
    flight_classification: entry.flight_classification || '',
    aircraft_registration: entry.aircraft_registration || '',
    flight_duration_minutes: entry.flight_duration_minutes || '',
    subject: entry.subject || '',
    examiner: entry.examiner_name || '',
    credit_minutes: entry.credit_minutes || '',
    credit_basis: entry.credit_basis || '',
    instructor: entry.assessor_name || payload.assessedByName || '',
    comments: entry.comments || payload.lessonNotes || '',
    next_lesson_focus: payload.nextLessonFocus || '',
    correction_reason: entry.correction_reason || '',
    corrects_entry_id: entry.supersedes_entry_id || '',
    booking_id: entry.booking_id || '',
    recorded_at: entry.assessed_at || entry.created_at || '',
  };
}

export function buildTrainingRecordCsvRows({
  studentName,
  courseName,
  versionCode,
  entries = [],
  exercises = [],
  items = [],
} = {}) {
  const rows = [];
  const exercisesById = new Map(
    exercises.map((exercise) => [String(exercise.id), exercise])
  );
  const itemsById = new Map(items.map((item) => [String(item.id), item]));
  entries
    .slice()
    .sort((left, right) => (
      String(left.recorded_on || '').localeCompare(String(right.recorded_on || '')) ||
      getAssessmentTimestamp(left) - getAssessmentTimestamp(right)
    ))
    .forEach((entry) => {
      const exerciseAssessments = Array.isArray(
        entry.record_payload?.exerciseAssessments
      )
        ? entry.record_payload.exerciseAssessments
        : [];
      const context = { studentName, courseName, versionCode };
      if (entry.record_type === 'lesson_assessment' && exerciseAssessments.length > 0) {
        exerciseAssessments.forEach((assessment) => {
          const syllabusItem = itemsById.get(String(assessment.itemId));
          const exercise = exercisesById.get(
            String(assessment.exerciseId || syllabusItem?.exercise_id)
          );
          rows.push(recordRow(entry, context, {
            ...assessment,
            exerciseReference:
              assessment.exerciseReference || exercise?.exercise_reference || '',
            exerciseTitle: assessment.exerciseTitle || exercise?.title || '',
            itemReference: assessment.itemReference || syllabusItem?.item_reference || '',
          }));
        });
      } else {
        rows.push(recordRow(entry, context));
      }
    });
  return rows;
}

export function createTrainingRecordCsv(options) {
  return Papa.unparse(buildTrainingRecordCsvRows(options), {
    columns: [
      'student',
      'course',
      'syllabus_version',
      'date',
      'type',
      'exercise_reference',
      'exercise_title',
      'item_reference',
      'assessment_outcome',
      'flight_classification',
      'aircraft_registration',
      'flight_duration_minutes',
      'subject',
      'examiner',
      'credit_minutes',
      'credit_basis',
      'instructor',
      'comments',
      'next_lesson_focus',
      'correction_reason',
      'corrects_entry_id',
      'booking_id',
      'recorded_at',
    ],
  });
}

export function downloadTrainingRecordCsv(options) {
  const csv = createTrainingRecordCsv(options);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'wingit-training-record.csv';
  link.click();
  URL.revokeObjectURL(url);
}
